import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Checkbox,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
  TextContent,
  Textarea,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationUpdateRequest,
  RelatedInvestigation,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import {
  humanTransitions,
  isTerminal,
  RESOLUTION_REASONS,
  STATUS_LABELS,
  RedriveHistory,
  formatTimestamp,
  redriveHistory,
  resolutionReasonLabel,
  shownStatus,
} from "./investigationUtils";
import {
  InvestigationStatusStepper,
  orderTransitions,
  transitionLabel,
} from "./InvestigationStatusStepper";
import { MonitoringRedriveWatch } from "./MonitoringRedriveWatch";
import { BlockerReasonField } from "./BlockerReasonField";
import {
  AfterDeploy,
  CodeReviewFollowUp,
  CodeReviewWait,
  codeReviewLinkHint,
  defaultBlockerReason,
  normalizeBlockerLink,
  parseCodeReviewId,
  waitingOnCodeReview,
} from "./CodeReviewBlocker";
import { ReminderNote, RemindMeButton } from "./RemindMeButton";
import { BackToCbeCheck } from "./BackToCbeCheck";
import {
  awaitingConfirmation,
  documentRedriveRunning,
  readyForDlq,
} from "./documentRedrive";
import { saveErrorText } from "./versionConflict";
import { monitoringClear, monitoringClearSummary } from "./readyToResolve";
import { statusHint } from "./statusHint";
import { useCommentLinks } from "./evidenceLinks";

interface InvestigationStatusProps {
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onError: (message: string) => void;
  /** Opens the "Population and redrives" tab; enables the "View attempts" link. */
  onViewRedrives?: () => void;
  /** Open the Population and redrives tab: "choose" opens the redrive chooser, "check" the CBE check, "clear" the DLQ redrive. */
  onOpenRedrive?: (action: "choose" | "check" | "clear") => void;
  /** Rendered at the bottom of the card, e.g. the population rule. */
  footer?: React.ReactNode;
  /** Stepper layout: vertical for a narrow column, horizontal for a wide card. */
  orientation?: "horizontal" | "vertical";
  /** InvestigationDetail.related, passed to the close dialog. */
  related?: Array<RelatedInvestigation> | null;
}

/** Only http(s) URLs are rendered as links; anything else (javascript:, data:, …) is shown as text. */
export function isHttpLink(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

/**
 * "We already tried a redrive" — shown when the case is back on the investigative side of the
 * path after one or more attempts, so the transitions below aren't read as a first pass.
 */
export const InvestigationRedriveHistoryAlert: React.FC<{
  redrive: RedriveHistory;
  onViewRedrives?: () => void;
}> = ({ redrive, onViewRedrives }) => {
  const noun = redrive.kind === "DELETE" ? "Deletion" : "Redrive";
  const when = redrive.startedAt
    ? ` on ${formatTimestamp(redrive.startedAt)}`
    : "";
  const attempt = `${noun} attempt ${redrive.attempts}${when}`;
  let outcome: string;
  let type: "error" | "warning" = "warning";
  if (redrive.outcome === "failed") {
    outcome = `${attempt} failed before completing.`;
    type = "error";
  } else if (redrive.outcome === "returned") {
    outcome = `${attempt} completed, but ${redrive.returnedCount} item${
      redrive.returnedCount === 1 ? "" : "s"
    } returned to the DLQ.`;
  } else {
    outcome = `${attempt} completed; the owner moved the investigation back afterwards.`;
  }
  return (
    <Alert
      type={type}
      header={`${noun} already attempted (${redrive.attempts})`}
      data-testid="redrive-history"
    >
      {outcome}{" "}
      {onViewRedrives && (
        <Button variant="inline-link" onClick={onViewRedrives}>
          View attempts
        </Button>
      )}
    </Alert>
  );
};

/** Secondary meta line ("author · time · source") rendered under an entry. */
const MetaLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box color="text-body-secondary" fontSize="body-s">
    {children}
  </Box>
);

interface StatusTransitionModalProps {
  targetStatus: string;
  investigation: Investigation;
  loading: boolean;
  onDismiss: () => void;
  onSubmit: (request: InvestigationUpdateRequest) => void;
  /** Reopening someone else's (or nobody's) closed case makes the caller its owner. */
  takesOwnership?: boolean;
  /** InvestigationDetail.related: closing notes how many cases split from this one stay open. */
  related?: Array<RelatedInvestigation> | null;
  /** Lets Resolve offer the CR / ticket links written in comments as evidence. */
  stage?: Stage;
  /** Why the last submit was refused (for example, a CR that is already deployed). */
  error?: string;
}

export const StatusTransitionModal: React.FC<StatusTransitionModalProps> = ({
  targetStatus,
  investigation,
  loading,
  onDismiss,
  onSubmit,
  takesOwnership = false,
  related,
  stage,
  error,
}) => {
  const linkedCodeReview = (value: string) =>
    investigation.codeReviewTracking ? parseCodeReviewId(value) : null;
  const previousCodeReviewId = linkedCodeReview(
    investigation.blocker?.link || ""
  );
  const [reason, setReason] = useState(() => {
    const previous = investigation.blocker?.reason || "";
    return previousCodeReviewId &&
      previous ===
        defaultBlockerReason(previousCodeReviewId, investigation.stage)
      ? ""
      : previous;
  });
  const [link, setLink] = useState(
    previousCodeReviewId ? "" : investigation.blocker?.link || ""
  );
  const [nextReview, setNextReview] = useState(
    investigation.blocker?.nextReviewDate || ""
  );
  // A CR in the link is waited on: Lusca follows it and acts once it is deployed to the stage.
  const [afterDeploy, setAfterDeploy] = useState<AfterDeploy>("NONE");
  const codeReviewId = linkedCodeReview(link);
  const changeLink = (value: string) => {
    const previousDefault = codeReviewId
      ? defaultBlockerReason(codeReviewId, investigation.stage)
      : null;
    const nextId = linkedCodeReview(value);
    setLink(value);
    if (nextId !== codeReviewId) setAfterDeploy("NONE");
    if (nextId && (!reason.trim() || reason === previousDefault))
      setReason(defaultBlockerReason(nextId, investigation.stage));
    // The CR was replaced by a ticket: don't leave its untouched default reason behind.
    else if (!nextId && previousDefault && reason === previousDefault)
      setReason("");
  };
  const [resolutionReason, setResolutionReason] = useState(
    RESOLUTION_REASONS[0].value
  );
  const [summary, setSummary] = useState(
    investigation.conclusion?.summary || ""
  );
  const hasConclusion = !!summary.trim();
  const resolving = targetStatus === "RESOLVED";
  const commentLinks = useCommentLinks(
    stage,
    investigation.id,
    investigation.links || [],
    resolving
  );
  // Comment links start ticked; unticking one leaves it out of the evidence.
  const [skippedLinks, setSkippedLinks] = useState<Set<string>>(new Set());
  const pickedLinks = commentLinks.links.filter(
    (l) => !skippedLinks.has(l.url)
  );
  const hasEvidence =
    (investigation.links || []).length > 0 || pickedLinks.length > 0;
  // Withdrawing is its own terminal status: it claims no outcome (so takes no resolution reason
  // and the evidence warning has nothing to warn about) and the case is never "resolved".
  const withdrawing = targetStatus === "WITHDRAWN";
  const closing = resolving || withdrawing;
  // Only a resolution that claims a fix shipped needs a CR/ticket; the others have nothing to link.
  const claimsFix = resolutionReason.startsWith("FIXED_");
  // Split-out cases are independent work: closing this one leaves them as they are.
  const openSplitOuts = (related || []).filter(
    (r) => r.relation === "child" && r.status && !isTerminal(r.status)
  ).length;

  const submit = () => {
    const request: InvestigationUpdateRequest = {
      expectedVersion: investigation.version,
      status: targetStatus as InvestigationUpdateRequest["status"],
    };
    if (targetStatus === "BLOCKED") {
      request.blocker = {
        reason: reason.trim(),
        link: normalizeBlockerLink(link),
        nextReviewDate: nextReview || undefined,
        codeReview: codeReviewId
          ? {
              id: codeReviewId,
              afterDeploy,
            }
          : undefined,
      };
    }
    if (resolving) {
      request.resolutionReason =
        resolutionReason as InvestigationUpdateRequest["resolutionReason"];
      if (pickedLinks.length)
        request.addLinks = pickedLinks.map(({ url }) => ({ url }));
    }
    if (
      closing &&
      summary.trim() &&
      summary.trim() !== investigation.conclusion?.summary
    )
      request.conclusion = {
        ...(investigation.conclusion || {}),
        summary: summary.trim(),
      };
    onSubmit(request);
  };

  // The summary is the one hard requirement when closing a case; evidence is advisory.
  const disabled =
    (targetStatus === "BLOCKED" && !reason.trim()) ||
    (closing && !hasConclusion);

  const confirmLabel = withdrawing ? "Cancel and release items" : "Confirm";

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header={
        resolving
          ? "Resolve investigation"
          : withdrawing
          ? "Cancel investigation"
          : `Move to ${STATUS_LABELS[targetStatus]}`
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              {withdrawing ? "Keep investigating" : "Cancel"}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={disabled}
              loading={loading}
            >
              {confirmLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error && (
          <Alert type="error" data-testid="status-transition-error">
            {error}
          </Alert>
        )}
        {targetStatus === "BLOCKED" && (
          <>
            <BlockerReasonField value={reason} onChange={setReason} />
            <FormField
              label="Ticket or external link (optional)"
              description={
                investigation.codeReviewTracking
                  ? codeReviewLinkHint(investigation.stage)
                  : undefined
              }
            >
              <Input
                value={link}
                onChange={({ detail }) => changeLink(detail.value)}
                placeholder={
                  investigation.codeReviewTracking
                    ? "https://issues.amazon.com/… or CR-123456789"
                    : "https://issues.amazon.com/…"
                }
              />
            </FormField>
            {codeReviewId && (
              <CodeReviewFollowUp
                investigation={investigation}
                codeReviewId={codeReviewId}
                afterDeploy={afterDeploy}
                onAfterDeployChange={setAfterDeploy}
                backTo={
                  STATUS_LABELS[investigation.status] || investigation.status
                }
              />
            )}
            <FormField label="Next review date (optional)">
              <DatePicker
                value={nextReview}
                onChange={({ detail }) => setNextReview(detail.value)}
                placeholder="YYYY/MM/DD"
              />
            </FormField>
          </>
        )}
        {closing && (
          <>
            {resolving && (
              <FormField label="Resolution reason">
                <Select
                  selectedOption={
                    RESOLUTION_REASONS.find(
                      (r) => r.value === resolutionReason
                    ) || RESOLUTION_REASONS[0]
                  }
                  onChange={({ detail }) =>
                    setResolutionReason(
                      detail.selectedOption.value || RESOLUTION_REASONS[0].value
                    )
                  }
                  options={RESOLUTION_REASONS}
                />
              </FormField>
            )}
            <FormField
              label="Conclusion summary"
              description={
                withdrawing
                  ? "Why this investigation is being cancelled — shown to whoever picks these items up next."
                  : "What was found and what was done about it."
              }
              errorText={!hasConclusion ? "Required" : undefined}
            >
              <Textarea
                value={summary}
                onChange={({ detail }) => setSummary(detail.value)}
                rows={2}
              />
            </FormField>
            {resolving && commentLinks.links.length > 0 && (
              // Not a FormField: its label would name every checkbox the same.
              <div data-testid="comment-links">
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">
                    Add links from comments to the conclusion
                  </Box>
                  {commentLinks.links.map((l) => (
                    // The link sits beside the checkbox, not in its label, so opening a
                    // CR to check it does not untick it.
                    <SpaceBetween key={l.url} direction="horizontal" size="xxs">
                      <Checkbox
                        ariaLabel={`Add ${l.label}`}
                        checked={!skippedLinks.has(l.url)}
                        onChange={({ detail }) =>
                          setSkippedLinks((prev) => {
                            const next = new Set(prev);
                            if (detail.checked) next.delete(l.url);
                            else next.add(l.url);
                            return next;
                          })
                        }
                      />
                      <Link href={l.url} external>
                        {l.label}
                      </Link>
                      {l.author && (
                        <Box color="text-body-secondary" fontSize="body-s">
                          · {l.author}
                        </Box>
                      )}
                    </SpaceBetween>
                  ))}
                </SpaceBetween>
              </div>
            )}
            {openSplitOuts > 0 && (
              <Alert type="info" data-testid="open-split-outs">
                {openSplitOuts === 1
                  ? "1 case split from this one stays open."
                  : `${openSplitOuts} cases split from this one stay open.`}{" "}
                {withdrawing ? "Cancelling" : "Resolving"} this case does not
                close them.
              </Alert>
            )}
            {withdrawing && (
              <Alert type="info">
                Releases all {investigation.assignedCount ?? 0} items back to
                the uninvestigated pool. Not counted as resolved.
              </Alert>
            )}
            {resolving && claimsFix && !hasEvidence && (
              <Alert type="warning">
                No CR or ticket linked for this fix. You can add one on the
                Conclusion tab.
              </Alert>
            )}
          </>
        )}
        {targetStatus !== "BLOCKED" && !closing && (
          <TextContent>
            <p>Move the investigation to {STATUS_LABELS[targetStatus]}?</p>
            {takesOwnership && (
              <p data-testid="reopen-takes-ownership">
                You will become the owner
                {investigation.owner
                  ? ` (${investigation.owner} stays on as a contributor)`
                  : ""}
                .
              </p>
            )}
          </TextContent>
        )}
      </SpaceBetween>
    </Modal>
  );
};

/**
 * The Status card: current position on the main path, blocker / resolution, and the owner's
 * transitions. Everything but Resolve sits in one "Change status" dropdown so the row never
 * wraps; Resolve is its own button because it is the one action with a different weight.
 * Comments and the Conclusion live in their own tabs, rendered by the page.
 */
export const InvestigationStatus: React.FC<InvestigationStatusProps> = ({
  stage,
  investigation,
  currentUser,
  onError,
  onViewRedrives,
  onOpenRedrive,
  footer,
  orientation = "vertical",
  related,
}) => {
  const update = useUpdateInvestigation(stage);
  const isOwner = !!currentUser && investigation.owner === currentUser;
  // Nobody to defer to on an unowned case, so anyone may close it (resolve or cancel);
  // every other status change still needs an owner.
  const mayCloseOnly = !isOwner && !!currentUser && !investigation.owner;
  const closedStatus =
    investigation.status === "RESOLVED" || investigation.status === "WITHDRAWN";
  // Anyone may reopen a closed case (the backend makes them the owner).
  const mayReopenOnly = !isOwner && !!currentUser && closedStatus;
  const [statusModal, setStatusModal] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string>();

  const run = (
    request: InvestigationUpdateRequest,
    after?: () => void,
    onFailure: (message: string) => void = onError
  ) =>
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: { ...request, expectedVersion: investigation.version },
      })
      .then(() => after?.())
      .catch((err: InvestigationApiError) => onFailure(saveErrorText(err)));

  // A merged source or a closed duplicate never gets its items back: the backend refuses to
  // reopen either, so don't offer it.
  const permanentlyClosed =
    !!investigation.mergedIntoInvestigationId ||
    !!investigation.duplicateOfInvestigationId;
  const allTransitions = permanentlyClosed
    ? []
    : orderTransitions(
        investigation.status,
        humanTransitions(
          investigation.status,
          investigation.blocker?.blockedFrom
        ),
        investigation.blocker?.blockedFrom
      );
  const transitions = mayReopenOnly
    ? allTransitions.filter((t) => t === "INVESTIGATING")
    : mayCloseOnly
    ? allTransitions.filter((t) => t === "RESOLVED" || t === "WITHDRAWN")
    : allTransitions;
  const label = (to: string) =>
    transitionLabel(
      investigation.status,
      to,
      investigation.blocker?.blockedFrom
    );
  const canResolve = transitions.includes("RESOLVED");
  const otherTransitions = transitions.filter((t) => t !== "RESOLVED");
  const redrive = redriveHistory(investigation);
  const quiet = monitoringClear(investigation);
  const hint = statusHint(investigation, isOwner);

  return (
    <Container
      fitHeight
      header={
        // Status changes sit with the Status heading, owner only (closing: anyone if unowned).
        <Header
          variant="h2"
          actions={
            (isOwner || mayCloseOnly || mayReopenOnly) &&
            transitions.length > 0 && (
              <SpaceBetween direction="horizontal" size="xs">
                {/* A single choice (e.g. Reopen on a closed case) is its own button, not a menu. */}
                {otherTransitions.length === 1 && (
                  <Button onClick={() => setStatusModal(otherTransitions[0])}>
                    {label(otherTransitions[0])}
                  </Button>
                )}
                {otherTransitions.length > 1 && (
                  <ButtonDropdown
                    ariaLabel="Change status"
                    expandToViewport
                    items={otherTransitions.map((status) => ({
                      id: status,
                      text: label(status),
                    }))}
                    onItemClick={({ detail }) => setStatusModal(detail.id)}
                  >
                    Change status
                  </ButtonDropdown>
                )}
                {/* Ready to resolve puts Resolve under the step instead: one button, not two. */}
                {canResolve && !quiet && (
                  <Button onClick={() => setStatusModal("RESOLVED")}>
                    {label("RESOLVED")}
                  </Button>
                )}
              </SpaceBetween>
            )
          }
        >
          Status
        </Header>
      }
    >
      <SpaceBetween size="m">
        <InvestigationStatusStepper
          status={shownStatus(investigation)}
          blockedFrom={investigation.blocker?.blockedFrom}
          waitingOn={waitingOnCodeReview(investigation)?.id}
          redrive={redrive}
          monitoringClear={quiet ? monitoringClearSummary(investigation) : null}
          onCurrentStepClick={
            !quiet &&
            (investigation.status === "MONITORING_REDRIVE" ||
              investigation.status === "EXECUTING_REDRIVE")
              ? onViewRedrives
              : undefined
          }
          onRedriveStepClick={onViewRedrives}
          currentDetails={
            quiet ? (
              // Monitoring passed: one clear next action under "Ready to resolve".
              <Box margin={{ top: "xs" }} data-testid="ready-to-resolve-action">
                <Button
                  variant="primary"
                  iconName="expand"
                  iconAlign="right"
                  ariaLabel="Resolve (opens a dialog)"
                  disabled={!canResolve}
                  disabledReason={
                    investigation.owner
                      ? `Only ${investigation.owner} (the owner) can resolve.`
                      : "Claim this investigation to resolve it."
                  }
                  onClick={() => setStatusModal("RESOLVED")}
                >
                  Resolve
                </Button>
              </Box>
            ) : investigation.status === "MONITORING_REDRIVE" ? (
              <MonitoringRedriveWatch
                stage={stage}
                investigation={investigation}
              />
            ) : investigation.status === "EXECUTING_REDRIVE" ? (
              // Quiet line under the running step (not a banner): nothing for the owner to do.
              <Box
                color="text-body-secondary"
                fontSize="body-s"
                data-testid="executing-redrive-hint"
              >
                {documentRedriveRunning(
                  investigation.status,
                  investigation.documentRedrive
                )
                  ? "Republishing the CBEs. Moves on by itself when done."
                  : "Requeuing the DLQ items. Moves on by itself when done."}
              </Box>
            ) : readyForDlq(
                investigation.status,
                investigation.documentRedrive
              ) ? (
              // Two separate steps: "Check CBEs" (Review → the check modal; bell = remind me to
              // check later), then "DLQ redrive" (Clear DLQ → the redrive dialog). Short labels
              // so nothing wraps in the narrow stepper column.
              <Box margin={{ top: "xs" }}>
                <SpaceBetween size="xxs" data-testid="ready-for-dlq-hint">
                  {isOwner && onOpenRedrive ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {awaitingConfirmation(investigation) ? (
                        <>
                          <Button
                            variant="primary"
                            iconName="expand"
                            iconAlign="right"
                            ariaLabel="Review the CBEs (opens a dialog)"
                            onClick={() => onOpenRedrive("check")}
                          >
                            Review
                          </Button>
                          <RemindMeButton
                            stage={stage}
                            investigation={investigation}
                            onError={onError}
                          />
                        </>
                      ) : (
                        <Button
                          variant="primary"
                          iconName="expand"
                          iconAlign="right"
                          ariaLabel="Clear the DLQ (opens a dialog)"
                          onClick={() => onOpenRedrive("clear")}
                        >
                          Clear DLQ
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Box color="text-body-secondary" fontSize="body-s">
                      {awaitingConfirmation(investigation)
                        ? "Waiting for the owner to check the CBEs."
                        : "Waiting for the owner to clear the DLQ."}
                    </Box>
                  )}
                  {awaitingConfirmation(investigation) ? (
                    <ReminderNote investigation={investigation} />
                  ) : (
                    isOwner && (
                      <BackToCbeCheck
                        stage={stage}
                        investigation={investigation}
                        onError={onError}
                      />
                    )
                  )}
                </SpaceBetween>
              </Box>
            ) : investigation.status === "READY_TO_REDRIVE" && onOpenRedrive ? (
              // The fix is in: one clear next action under the step. The expand icon says it
              // opens a dialog (the redrive chooser); everyone sees it, only the owner can use it.
              <Box margin={{ top: "xs" }} data-testid="ready-to-redrive-action">
                <Button
                  variant="primary"
                  iconName="expand"
                  iconAlign="right"
                  ariaLabel="Redrive (opens a dialog)"
                  disabled={!isOwner || !!investigation.pendingDeletion}
                  disabledReason={
                    investigation.pendingDeletion
                      ? "A delete-without-redrive request is pending; execute or cancel it first."
                      : investigation.owner
                      ? `Only ${investigation.owner} (the owner) can redrive.`
                      : "Claim this investigation to redrive it."
                  }
                  onClick={() => onOpenRedrive("choose")}
                >
                  Redrive
                </Button>
              </Box>
            ) : undefined
          }
          orientation={orientation}
          unowned={!investigation.owner}
          documentRedrive={investigation.documentRedrive}
        />
        {hint && (
          <Box
            variant="small"
            color="text-body-secondary"
            data-testid="status-hint"
          >
            {hint}
          </Box>
        )}
        {/* Only when the last attempt needs attention (failed / items returned); a clean
            attempt the owner moved on from is just history, shown on the stepper step. */}
        {redrive &&
          (redrive.outcome === "failed" || redrive.outcome === "returned") && (
            <InvestigationRedriveHistoryAlert
              redrive={redrive}
              onViewRedrives={onViewRedrives}
            />
          )}
        {investigation.blocker?.reason &&
          investigation.status === "BLOCKED" && (
            <Alert type="error" header="Blocked">
              {investigation.blocker.reason}
              {investigation.blocker.link && (
                <>
                  {" · "}
                  {isHttpLink(investigation.blocker.link) ? (
                    <Link href={investigation.blocker.link} external>
                      ticket / link
                    </Link>
                  ) : (
                    // Not http(s) (e.g. javascript:): Cloudscape Link throws on it, so plain text.
                    <span>{investigation.blocker.link}</span>
                  )}
                </>
              )}
              {investigation.blocker.nextReviewDate &&
                ` · next review ${investigation.blocker.nextReviewDate}`}
              {investigation.blocker.blockedFrom === "MONITORING_REDRIVE" && (
                <Box variant="p" margin={{ top: "xs" }}>
                  Lusca keeps monitoring the redrive while blocked; a detected
                  return clears the block and reopens the investigation.
                </Box>
              )}
              {investigation.blocker.codeReview && (
                <Box margin={{ top: "xs" }}>
                  <CodeReviewWait
                    investigation={investigation}
                    isOwner={isOwner}
                    loading={update.isLoading}
                    onUpdate={(request) => {
                      run({
                        ...request,
                        expectedVersion: investigation.version,
                      }).catch(console.error);
                    }}
                  />
                </Box>
              )}
            </Alert>
          )}
        {investigation.resolution && investigation.status === "WITHDRAWN" && (
          <Alert type="info" header="Cancelled">
            by {investigation.resolution.resolvedBy} on{" "}
            {formatTimestamp(investigation.resolution.resolvedAt)}. The items
            are back in the uninvestigated pool; this case is not counted as
            resolved.
          </Alert>
        )}
        {investigation.resolution && investigation.status !== "WITHDRAWN" && (
          <Alert
            type="success"
            header={`Resolved: ${resolutionReasonLabel(
              investigation.resolution.reason
            ).toLowerCase()}`}
          >
            by {investigation.resolution.resolvedBy} on{" "}
            {formatTimestamp(investigation.resolution.resolvedAt)}
          </Alert>
        )}
        {/* Unowned: the banner, the stepper's "Needs an owner" step and the Details badge
            already say so — no third copy here. */}
        {!isOwner && investigation.owner && !mayReopenOnly && (
          <MetaLine>
            Only the owner ({investigation.owner}) can change the status. You
            can contribute in the comments.
          </MetaLine>
        )}
        {footer}
      </SpaceBetween>

      {statusModal && (
        <StatusTransitionModal
          targetStatus={statusModal}
          stage={stage}
          investigation={investigation}
          loading={update.isLoading}
          takesOwnership={mayReopenOnly}
          related={related}
          error={statusError}
          onDismiss={() => {
            setStatusModal(null);
            setStatusError(undefined);
          }}
          onSubmit={(request) => {
            setStatusError(undefined);
            // A refusal (such as a CR that is already deployed) shows in the open form.
            run(
              request,
              () => {
                setStatusModal(null);
                // Ready to redrive is only a step towards the redrive: go straight to choosing it.
                if (request.status === "READY_TO_REDRIVE")
                  onOpenRedrive?.("choose");
              },
              setStatusError
            ).catch(console.error);
          }}
        />
      )}
    </Container>
  );
};

export default InvestigationStatus;
